import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryActionCards } from '@/components/DiaryActionCards';
import { DiaryDayLessons } from '@/components/DiaryDayLessons';
import { LessonTimesChip } from '@/components/DiaryHeader/LessonTimesChip';
import { PeriodPicker } from '@/components/DiaryHeader/PeriodPicker';
import { DiaryDay } from '@/components/DiaryTable/DiaryDay';
import { DiaryEmptyDay } from '@/components/DiaryTable/DiaryEmptyDay';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { HomeDiaryPaywall } from '@/pages/Home/HomeDiaryPaywall';
import { useHomeDiary } from '@/pages/Home/hooks/useHomeDiary';
import { SCROLL_TO_START, scrollToElement } from '@/utils/scroll';
import { SectionTitle } from '@/pages/Home/SectionTitle';

export const HomeDiary: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const hasSubscription = useHasFamilySubscription();
  const topPickerRef = useRef<HTMLDivElement>(null);
  const {
    isDiaryAvailable,
    isLoading,
    desktopEntries,
    mobileEntry,
    pickerLabel,
    pickerValue,
    minDate,
    maxDate,
    selectDate,
    goToPrevious,
    goToNext,
  } = useHomeDiary();

  if (!hasSubscription) {
    return (
      <section className="flex flex-col gap-4 px-4 pt-6 md:px-6 md:pt-10">
        <SectionTitle>{t('home.diary.title')}</SectionTitle>
        <HomeDiaryPaywall />
      </section>
    );
  }

  if (!isDiaryAvailable) {
    return null;
  }

  const hasBottomPicker = !isLoading && (isMobile || !!desktopEntries.length);

  const scrollToTopPicker = () =>
    scrollToElement(topPickerRef.current, SCROLL_TO_START);

  const renderPicker = (isBottom: boolean) => (
    <PeriodPicker
      label={pickerLabel}
      previousLabel={t('diary.previousDay')}
      nextLabel={t('diary.nextDay')}
      value={pickerValue}
      minDate={minDate}
      maxDate={maxDate}
      hasCalendarAbove={isBottom}
      className={isMobile ? 'w-full' : 'w-fit'}
      onChange={selectDate}
      onPrevious={goToPrevious}
      onNext={goToNext}
      onBeforeChange={isBottom ? scrollToTopPicker : undefined}
    />
  );

  return (
    <section className="flex flex-col px-4 pt-6 md:px-6 md:pt-10">
      <div className="flex items-start justify-between gap-4 md:items-center">
        <SectionTitle>{t('home.diary.title')}</SectionTitle>
        {isMobile && <LessonTimesChip className="mt-px" />}
        {!isMobile && (
          <div className="flex shrink-0 gap-2">
            <DiaryActionCards isCompact />
          </div>
        )}
      </div>

      {isMobile && (
        <div className="flex gap-2 pt-3">
          <DiaryActionCards isCompact />
        </div>
      )}

      <div
        ref={topPickerRef}
        className="flex scroll-mt-4 items-end justify-between gap-4 pt-4 pb-2 md:pb-4"
      >
        {renderPicker(false)}
        {!isMobile && <LessonTimesChip />}
      </div>

      {isLoading && (
        <div className="flex h-[250px] items-center justify-center md:h-[300px]">
          <PageSpinner className="size-[20px] text-subtle-foreground" />
        </div>
      )}

      {!isLoading && isMobile && <DiaryDayLessons entry={mobileEntry} />}

      {!isLoading && !isMobile && !desktopEntries.length && <DiaryEmptyDay />}

      {!isLoading && !isMobile && (
        <div className="flex flex-col [&>*:last-child]:mb-0">
          {desktopEntries.map((entry) => (
            <DiaryDay key={entry.date} entry={entry} />
          ))}
        </div>
      )}

      {hasBottomPicker && (
        <div className="flex items-end justify-between gap-4 pt-2 md:pt-4">
          {renderPicker(true)}
          {!isMobile && <LessonTimesChip />}
        </div>
      )}
    </section>
  );
};
